// ABOUTME: Chart component for embedding charts in reports.
// ABOUTME: Uses Recharts to render bar, line, area, and pie charts.

import {
  BarChart,
  Bar,
  LineChart,
  Line,
  AreaChart,
  Area,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';

interface ChartData {
  label: string;
  value: number;
  prev?: number;
  group?: string;
}

interface ReportChartProps {
  /** Simple single-series data ({label, value}). Use this OR rawData, not both. */
  data?: ChartData[];
  /** Raw multi-series data from insights (e.g., {hour: "12am", Monday: 63, Tuesday: 62}).
   *  When provided, auto-detects category key and numeric series. */
  rawData?: Record<string, any>[];
  type?: 'bar' | 'line' | 'area' | 'pie';
  title?: string;
  height?: number;
  showLegend?: boolean;
}

const CHART_COLORS = [
  '#14b8a6', // teal (primary brand)
  '#818CF8', // indigo/periwinkle
  '#F59E0B', // amber/gold
  '#F43F5E', // coral/rose
  '#60A5FA', // sky blue
  '#A78BFA', // violet/lavender
  '#FB923C', // tangerine
  '#34D399', // emerald
  '#E879F9', // fuchsia/pink
  '#06B6D4', // cyan
];

// Format axis tick values to avoid label overflow. Abbreviates large numbers
// (e.g. 1200 → 1.2K, 2500000 → 2.5M) and keeps small numbers as-is.
function formatTickValue(value: number): string {
  const abs = Math.abs(value);
  if (abs >= 1_000_000_000) {
    const formatted = (value / 1_000_000_000).toFixed(1);
    return `${formatted.replace(/\.0$/, '')}B`;
  }
  if (abs >= 1_000_000) {
    const formatted = (value / 1_000_000).toFixed(1);
    return `${formatted.replace(/\.0$/, '')}M`;
  }
  if (abs >= 1_000) {
    const formatted = (value / 1_000).toFixed(1);
    return `${formatted.replace(/\.0$/, '')}K`;
  }
  return value.toLocaleString();
}

// Estimate the pixel width needed for y-axis tick labels based on the
// longest formatted tick string in the data.
function estimateYAxisWidth(data: { value: number; prev?: number; name?: string }[]): number {
  if (data.length === 0) return 40;
  let maxVal = 0;
  for (const d of data) {
    if (Math.abs(d.value) > maxVal) maxVal = Math.abs(d.value);
    if (d.prev !== undefined && Math.abs(d.prev) > maxVal) maxVal = Math.abs(d.prev);
  }
  const formatted = formatTickValue(maxVal);
  // ~8px per character at fontSize 12, plus 8px padding
  return Math.max(40, formatted.length * 8 + 8);
}

const MAX_LABEL_CHARS = 14;

// Truncate a label to MAX_LABEL_CHARS, appending "…" if shortened.
// Full labels remain available in tooltips.
function truncateLabel(label: string): string {
  return label.length > MAX_LABEL_CHARS
    ? label.slice(0, MAX_LABEL_CHARS - 1) + '…'
    : label;
}

// Decide how to render x-axis labels based on data volume and label length.
// Few items with long labels → angle, truncate, and force-show all.
// Many items (time series) → let Recharts auto-thin, no angle.
function computeXAxisLayout(labels: string[]): { angle: number; bottomMargin: number; forceAllLabels: boolean } {
  if (labels.length === 0) return { angle: 0, bottomMargin: 20, forceAllLabels: false };
  if (labels.length > 20) return { angle: 0, bottomMargin: 20, forceAllLabels: false };
  const validLabels = labels.filter((l): l is string => l != null && typeof l === 'string');
  if (validLabels.length === 0) return { angle: 0, bottomMargin: 20, forceAllLabels: false };
  const maxLen = Math.max(...validLabels.map((l) => l.length));
  const avgLen = validLabels.reduce((s, l) => s + l.length, 0) / validLabels.length;
  const needsAngle = validLabels.length > 4 && avgLen > 6 || maxLen > 12;
  if (needsAngle) {
    const displayLen = Math.min(maxLen, MAX_LABEL_CHARS);
    const extraHeight = Math.min(displayLen * 3, 50);
    return { angle: -30, bottomMargin: 20 + extraHeight, forceAllLabels: true };
  }
  return { angle: 0, bottomMargin: 20, forceAllLabels: true };
}

/**
 * Detect category key (first string-valued) and series keys (numeric) from data.
 */
function detectMultiSeriesKeys(data: Record<string, any>[]): { categoryKey: string; seriesKeys: string[] } {
  if (!data.length) return { categoryKey: '', seriesKeys: [] };
  const first = data[0];
  let categoryKey = '';
  const seriesKeys: string[] = [];
  for (const [key, val] of Object.entries(first)) {
    if (typeof val === 'string' && !categoryKey) {
      categoryKey = key;
    } else if (typeof val === 'number') {
      seriesKeys.push(key);
    }
  }
  return { categoryKey, seriesKeys };
}

export function ReportChart({
  data,
  rawData,
  type = 'bar',
  title,
  height = 300,
  showLegend = true,
}: ReportChartProps) {
  // If rawData provided, use multi-series rendering
  const isMultiSeries = !!rawData && rawData.length > 0;

  const chartData = isMultiSeries
    ? rawData!
    : (data || []).map((d) => ({
        name: d.label,
        value: d.value,
        prev: d.prev,
      }));

  // Multi-series setup
  const { categoryKey, seriesKeys } = isMultiSeries
    ? detectMultiSeriesKeys(rawData!)
    : { categoryKey: 'name', seriesKeys: ['value'] };

  const tooltipStyle = {
    backgroundColor: 'rgba(30, 31, 38, 0.95)',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: '8px',
    color: '#f1f5f9',
  };

  // For multi-series, axis sizing is handled generically; for single-series, use dynamic sizing
  const singleSeriesData = isMultiSeries ? [] : (chartData as { name: string; value: number; prev?: number }[]);
  const yAxisWidth = estimateYAxisWidth(singleSeriesData);
  const { angle: xAngle, bottomMargin, forceAllLabels } = computeXAxisLayout(
    singleSeriesData.map((d) => d.name)
  );

  const yAxisProps = {
    stroke: '#64748b',
    tick: { fill: '#94a3b8', fontSize: 12 },
    tickFormatter: formatTickValue,
    width: yAxisWidth,
  };

  const xAxisProps = {
    dataKey: 'name' as const,
    stroke: '#64748b',
    tick: { fill: '#94a3b8', fontSize: 12 },
    tickFormatter: truncateLabel,
    ...(forceAllLabels && { interval: 0 as const }),
    ...(xAngle !== 0 && {
      angle: xAngle,
      textAnchor: 'end' as const,
      height: bottomMargin,
    }),
  };

  const renderChart = () => {
    switch (type) {
      case 'line':
        return (
          <LineChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
            <XAxis {...xAxisProps} dataKey={isMultiSeries ? categoryKey : 'name'} />
            <YAxis {...yAxisProps} />
            <Tooltip contentStyle={tooltipStyle} />
            {showLegend && <Legend />}
            {isMultiSeries ? (
              seriesKeys.map((key, i) => (
                <Line key={key} type="monotone" dataKey={key} stroke={CHART_COLORS[i % CHART_COLORS.length]} strokeWidth={2} dot={false} />
              ))
            ) : (
              <>
                <Line type="monotone" dataKey="value" stroke={CHART_COLORS[0]} strokeWidth={2} dot={{ fill: CHART_COLORS[0], strokeWidth: 2 }} activeDot={{ r: 6 }} />
                {chartData.some((d: any) => d.prev !== undefined) && (
                  <Line type="monotone" dataKey="prev" stroke={CHART_COLORS[1]} strokeWidth={2} strokeDasharray="5 5" dot={{ fill: CHART_COLORS[1], strokeWidth: 2 }} />
                )}
              </>
            )}
          </LineChart>
        );

      case 'area':
        return (
          <AreaChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
            <XAxis {...xAxisProps} dataKey={isMultiSeries ? categoryKey : 'name'} />
            <YAxis {...yAxisProps} />
            <Tooltip contentStyle={tooltipStyle} />
            {showLegend && <Legend />}
            {isMultiSeries ? (
              seriesKeys.map((key, i) => (
                <Area key={key} type="monotone" dataKey={key} stroke={CHART_COLORS[i % CHART_COLORS.length]} fill={`${CHART_COLORS[i % CHART_COLORS.length]}26`} strokeWidth={2} />
              ))
            ) : (
              <Area type="monotone" dataKey="value" stroke={CHART_COLORS[0]} fill={`${CHART_COLORS[0]}40`} strokeWidth={2} />
            )}
          </AreaChart>
        );

      case 'pie':
        return (
          <PieChart>
            <Pie data={chartData} cx="50%" cy="50%" innerRadius={60} outerRadius={100} paddingAngle={2} dataKey={isMultiSeries ? seriesKeys[0] : 'value'} label={({ name, percent }) => `${name ?? 'Unknown'} (${((percent ?? 0) * 100).toFixed(0)}%)`} labelLine={{ stroke: '#64748b' }}>
              {chartData.map((_: any, index: number) => (
                <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
              ))}
            </Pie>
            <Tooltip contentStyle={tooltipStyle} />
            {showLegend && <Legend />}
          </PieChart>
        );

      case 'bar':
      default:
        return (
          <BarChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
            <XAxis {...xAxisProps} dataKey={isMultiSeries ? categoryKey : 'name'} />
            <YAxis {...yAxisProps} />
            <Tooltip contentStyle={tooltipStyle} />
            {showLegend && <Legend />}
            {isMultiSeries ? (
              seriesKeys.map((key, i) => (
                <Bar key={key} dataKey={key} fill={CHART_COLORS[i % CHART_COLORS.length]} radius={[4, 4, 0, 0]} />
              ))
            ) : (
              <>
                <Bar dataKey="value" fill={CHART_COLORS[0]} radius={[4, 4, 0, 0]} />
                {chartData.some((d: any) => d.prev !== undefined) && (
                  <Bar dataKey="prev" fill={CHART_COLORS[1]} radius={[4, 4, 0, 0]} />
                )}
              </>
            )}
          </BarChart>
        );
    }
  };

  return (
    <div className="w-full">
      {title && (
        <h4 className="text-sm font-medium text-surface-600 mb-3">
          {title}
        </h4>
      )}
      <ResponsiveContainer width="100%" height={height}>
        {renderChart()}
      </ResponsiveContainer>
    </div>
  );
}
